/**
 * Utility to determine mandatory fields for B2B profile completion
 * Based on backend validation logic in userController.js
 */

export interface MandatoryFieldCheck {
  name: boolean;
  surname: boolean;
  phoneNumber: boolean;
  position: boolean;
  linkedin: boolean;
  businessName: boolean;
  businessCategory: boolean;
  countriesOfOperation: boolean;
  interestedIn: boolean;
}

/**
 * Check if a field is mandatory for profile completion
 */
export const isMandatoryField = (fieldName: string): boolean => {
  const mandatoryFields = [
    'name',
    'surname', 
    'phoneNumber',
    'position',
    'businessName',
    'businessCategory',
    'countriesOfOperation',
    'interestedIn'
  ];
  
  return mandatoryFields.includes(fieldName);
};

/**
 * Check if all mandatory fields are completed
 */
export const checkProfileCompletion = (formData: any): { isComplete: boolean; missingFields: string[] } => {
  const mandatoryChecks: MandatoryFieldCheck = {
    name: formData.name && formData.name.trim().length >= 2,
    surname: formData.surname && formData.surname.trim().length >= 2,
    phoneNumber: formData.phoneNumber && formData.phoneNumber.trim().length >= 7,
    position: Array.isArray(formData.position)
      ? formData.position.length > 0
      : (formData.position && formData.position.trim().length > 0),
    linkedin: true, // LinkedIn is now optional
    businessName: formData.businesses && 
                 formData.businesses.length > 0 && 
                 formData.businesses.some((business: any) => 
                   business.businessName && business.businessName.trim().length > 0
                 ),
    businessCategory: formData.businesses && 
                     formData.businesses.length > 0 && 
                     formData.businesses.some((business: any) => 
                       Array.isArray(business.businessCategory)
                         ? business.businessCategory.length > 0
                         : (business.businessCategory && business.businessCategory.trim().length > 0)
                     ),
    countriesOfOperation: formData.businesses && 
                         formData.businesses.length > 0 && 
                         formData.businesses.some((business: any) => 
                           business.countriesOfOperation && 
                           Array.isArray(business.countriesOfOperation) && 
                           business.countriesOfOperation.length > 0
                         ),
    interestedIn: formData.interestedIn && 
                 Array.isArray(formData.interestedIn) && 
                 formData.interestedIn.length > 0
  };

  const missingFields = Object.entries(mandatoryChecks)
    .filter(([field, isValid]) => !isValid)
    .map(([field]) => field);

  return {
    isComplete: missingFields.length === 0,
    missingFields
  };
};

/**
 * Get mandatory field names for a specific form section
 */
export const getMandatoryFieldsForSection = (section: 'personal' | 'business' | 'interests'): string[] => {
  switch (section) {
    case 'personal':
      return ['name', 'surname', 'phoneNumber', 'position'];
    case 'business':
      return ['businessName', 'businessCategory', 'countriesOfOperation'];
    case 'interests':
      return ['interestedIn'];
    default:
      return [];
  }
};
